import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const ANIMALS = [
    { index: 0, chinese: '子', english: 'Rat' },
    { index: 1, chinese: '丑', english: 'Ox' },
    { index: 2, chinese: '寅', english: 'Tiger' },
    { index: 3, chinese: '卯', english: 'Rabbit' },
    { index: 4, chinese: '辰', english: 'Dragon' },
    { index: 5, chinese: '巳', english: 'Snake' },
    { index: 6, chinese: '午', english: 'Horse' },
    { index: 7, chinese: '未', english: 'Goat' },
    { index: 8, chinese: '申', english: 'Monkey' },
    { index: 9, chinese: '酉', english: 'Rooster' },
    { index: 10, chinese: '戌', english: 'Dog' },
    { index: 11, chinese: '亥', english: 'Pig' },
];

const Register = () => {
    const { register } = useAuth();
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [birthYear, setBirthYear] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [calculatedAnimal, setCalculatedAnimal] = useState(null);

    // Calculate animal when birth year changes
    useEffect(() => {
        if (birthYear && birthYear >= 1900 && birthYear <= 2100) {
            const index = (birthYear - 4) % 12;
            setCalculatedAnimal(ANIMALS[index]);
        } else {
            setCalculatedAnimal(null);
        }
    }, [birthYear]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        if (password !== confirmPassword) {
            setError(language === 'zh' ? '密码不匹配' : 'Passwords do not match');
            setLoading(false);
            return;
        }
        
        if (password.length < 6) {
            setError(language === 'zh' ? '密码至少需要6个字符' : 'Password must be at least 6 characters');
            setLoading(false);
            return;
        }
        
        const result = await register(email, password, name, birthYear ? parseInt(birthYear) : null);
        
        if (result.success) {
            navigate('/calendar');
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    // Generate year options
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 1920; y--) {
        years.push(y);
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4 py-12">
            <div className="w-full max-w-md animate-fade-in">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block">
                        <h1 className="font-serif text-2xl font-bold">
                            <span className="font-chinese text-wealth">通书</span> Plotter
                        </h1>
                    </Link>
                </div>
                
                <Card className="border-2">
                    <CardHeader className="space-y-1">
                        <CardTitle className="font-serif text-2xl">
                            {language === 'zh' ? '创建账号' : 'Create Account'}
                        </CardTitle>
                        <CardDescription>
                            {language === 'zh' 
                                ? '注册以使用专业通书系统'
                                : 'Sign up to access the Professional Tong Shu system'
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm" data-testid="register-error">
                                    {error}
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label htmlFor="name">{t('name')}</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    placeholder={language === 'zh' ? '您的姓名' : 'Your name'}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    data-testid="register-name-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('email')}</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="register-email-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="birth-year">
                                    {language === 'zh' ? '出生年份（可选）' : 'Birth Year (Optional)'}
                                </Label>
                                <Select 
                                    value={birthYear ? birthYear.toString() : undefined} 
                                    onValueChange={(val) => setBirthYear(val ? parseInt(val) : '')}
                                >
                                    <SelectTrigger data-testid="register-birth-year-select">
                                        <SelectValue placeholder={language === 'zh' ? '选择出生年份' : 'Select birth year'} />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                        {years.map((year) => (
                                            <SelectItem key={year} value={year.toString()}>
                                                {year}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {calculatedAnimal && (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                        <span className="font-chinese">{calculatedAnimal.chinese}</span>
                                        <span>{calculatedAnimal.english}</span>
                                        <span className="text-xs">({language === 'zh' ? '您的生肖' : 'Your zodiac'})</span>
                                    </div>
                                )}
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="password">{t('password')}</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        data-testid="register-password-input"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        onClick={() => setShowPassword(!showPassword)}
                                        data-testid="toggle-password-btn"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                                <Input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    data-testid="register-confirm-password-input"
                                />
                            </div>
                            
                            <Button 
                                type="submit" 
                                className="w-full rounded-full" 
                                disabled={loading}
                                data-testid="register-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {language === 'zh' ? '创建中...' : 'Creating account...'}
                                    </>
                                ) : (
                                    t('signUp')
                                )}
                            </Button>
                        </form>
                        
                        <div className="mt-6 text-center text-sm">
                            <span className="text-muted-foreground">{t('haveAccount')}</span>{' '}
                            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                                {t('signIn')}
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Register;
